import { toLocalDateString } from "./dateUtils";

/**
 * Which of a client's weddings is "the current one" (M50).
 *
 * Pages used to take `weddings[0]`, i.e. whatever the server sort put first — a
 * repeat client could see last year's (or a cancelled) wedding's checklist,
 * timeline or budget. The rule, shared by every client page:
 *   1. the soonest non-cancelled wedding dated today or later (IST calendar day);
 *   2. otherwise the latest non-cancelled wedding;
 *   3. otherwise undefined.
 * The server sort is deliberately NOT changed (other readers depend on it).
 */
export interface WeddingLike {
  weddingDate?: string | Date | null;
  status?: string | null;
}

const isCancelled = (w: WeddingLike) => String(w?.status ?? '').toLowerCase() === 'cancelled';

export function pickCurrentWedding<T extends WeddingLike>(
  weddings: ReadonlyArray<T> | null | undefined,
  now: Date = new Date(),
): T | undefined {
  if (!Array.isArray(weddings) || weddings.length === 0) return undefined;
  const today = toLocalDateString(now);
  const active = weddings
    .filter((w) => w && !isCancelled(w))
    .map((w) => ({ w, day: toLocalDateString(w.weddingDate ?? null) }));
  if (active.length === 0) return undefined;

  const upcoming = active
    .filter((x) => x.day !== '' && x.day >= today)
    .sort((a, b) => a.day.localeCompare(b.day));
  if (upcoming.length > 0) return upcoming[0].w;

  // No upcoming wedding: the most recent one (undated rows sort last).
  const past = [...active].sort((a, b) => b.day.localeCompare(a.day));
  return past[0].w;
}

/**
 * PURE. What a wedding-scoped page should render (M24, M30). A failed lookup is
 * never rendered as "No wedding booked yet".
 */
export function weddingLookupState(opts: {
  hasWedding: boolean;
  loading: boolean;
  ownError: boolean;
  collabError: boolean;
  collabCount: number;
}): 'ready' | 'loading' | 'error' | 'empty' {
  if (opts.hasWedding) return 'ready';
  if (opts.loading) return 'loading';
  if ((opts.ownError && opts.collabCount === 0) || opts.collabError) return 'error';
  return 'empty';
}
